import { Injectable, OnDestroy } from '@angular/core';
import { isString } from 'lodash-es';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { retryAndRethrow, throwAsyncError } from 'webcommon/shared';
import { ApAlert, ApAlertType } from './ApAlert';

//
// Provides a service for showing alert messages.
// Supports displaying error, success and warning alerts.
//
// based on https://github.com/cornflourblue/angular-8-alert-notifications
//
// To use, add the <alert></alert> tag where you want alerts to be displayed.
//    <ng-container>
//      div class="col-sm-12" >
//        <ap-alert></ap-alert>
//      </div>
//    </ng-container>
//
// then add providers for ApAlertService to the component using <ap-alert></ap-alert>
//  @Component({providers: [
//      ApAlertService,
//    ],
//    ...
//
// AlertComponent optional inputs
//  fade - controls if alert messages are faded out when closed. Defaults to true.
//
// ApAlert with no optional parameters: this.alertService.success('It worked!');
// ApAlert with optional parameter: this.alertService.success('Save completed!', { autoClose: true, autoCloseTimeout: 10000 });
//

@Injectable()
export class ApAlertService implements OnDestroy {

  private clearSubject$ = new Subject<ApAlert>();
  private clearErrorSubject$ = new Subject<string>();
  private subject$ = new ReplaySubject<ApAlert>(6, 4000);  // keep last 6 alerts for 4 seconds
  readonly defaultId = 'default-alert';
  private defaultHtmlBaseId = 1;

  ngOnDestroy() {
    this.clearSubject$.complete();
    this.clearErrorSubject$.complete();
    this.subject$.complete();
  }

  // Used by ApAlertComponent only
  onAlert(): Observable<ApAlert> {
    return this.subject$;
  }

  // Used by ApAlertComponent only
  onClearAlert(): Observable<ApAlert> {
    return this.clearSubject$;
  }

  // Used by ApAlertComponent only
  onClearErrors(): Observable<string> {
    return this.clearErrorSubject$;
  }

  success(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Success, message }));
  }

  info(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Info, message }));
  }

  error(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Error, message }));
  }

  // same as error(), but allows unknown type for message, for unknown catch blocks.
  // This was mainly added for type safety to handle cases where we were previously passing a value of type any to error(),
  // but were assuming it would always be a string
  error2(message: unknown, options?: Partial<ApAlert>) {
    return this.error(isString(message) ? message: '', options);
  }

  warn(message: string, options?: Partial<ApAlert>) {
    return this.alert(new ApAlert({ ...options, type: ApAlertType.Warning, message }));
  }

  alert(alert: ApAlert) {
    alert.id = alert.id || this.defaultId;
    alert.htmlBaseId = alert.htmlBaseId || (this.defaultHtmlBaseId++).toString();
    this.subject$.next(alert);
    return alert;
  }

  clearAlert(alert?: ApAlert) {
    if (alert) {
      this.clearSubject$.next(alert);
    }
  }

  clearAllAlerts(id = this.defaultId) {
    this.subject$.next(new ApAlert({ id }));
  }

  clearAllErrorAlerts(id = this.defaultId) {
    this.clearErrorSubject$.next(id);
  }

  // Usage: for cold observables
  // Be aware that there are places where a refresh of a tile ends up not making a request at all
  // because the configuration doesn't allow it, so IF you want that to be considered a successful case,
  // then you may have to attach this operator further down the line to make sure it can clear the error.
  handleColdError$<T>(message: string, options?: Partial<ApAlert>) {
    return (source: Observable<T>): Observable<T> => {
      return source.pipe(
        tap({
          next: _value => {
            this.clearAllErrorAlerts(options?.id);
          },
          error: (_e: unknown) => {
            // could use isHttpErrorResponse to check the status, if we wanted to handle these cases where
            // the network connection is off or the appserver is down differently than other errors
            // isHttpErrorResponse(_e) && _e.status === 0
            this.error(message, options);
          },
        }),
      );
    };
  }

  // Usage: for cold observables, where you don't want to pass thru the error
  //
  // (this doesn't really make sense for use in the main stream of a host observable, because the only way
  // to keep it going is with retry())
  handleColdErrorWithDefault$<T>(defaultValue: T, message: string, options?: Partial<ApAlert>) {
    return (source: Observable<T>): Observable<T> => {
      return source.pipe(
        this.handleColdError$(message, options),
        catchError((error: unknown) => {
          throwAsyncError(error);
          return of(defaultValue);
        }),
      );
    };
  }

  // Usage: for hot observables
  // If retryCount is omitted, retry will try to resubscribe on errors infinite number of times.
  // Be careful using this when there could replayed values, because it could just keep retrying a request further down the stream from
  // the replayed value, and then end up in an error => retry => get replayed value => error loop
  handleHotError$<T>(message: string, options?: Partial<ApAlert>) {
    return (source: Observable<T>): Observable<T> => {
      return source.pipe(
        this.handleColdError$(message, options),
        retryAndRethrow(),
      );
    };
  }
}
